import Phaser from 'phaser';
import FishSystem from '../systems/fishSystem';
import JellyfishSystem from '../systems/jellyfishSystem';
import { AudioSystem } from '../systems/audio';
import { completeLevel, markPlaying } from '../systems/progress';
import {
  shouldShowTouchControls,
  getSettings,
} from '../systems/settings';

const WORLD_W = 3840;
const WORLD_H = 720;
const GROUND_Y = 640;
const MAX_VOLUME = 100;

export default class LevelOneScene extends Phaser.Scene {
  constructor() {
    super('LevelOneScene');
  }

  init() {
    this.volume = MAX_VOLUME;
    this.dropsCollected = 0;

    this.checkpoint = {
      x: 80,
      y: GROUND_Y - 40,
      volume: MAX_VOLUME,
    };

    this.invulnerableUntil = 0;
    this.levelState = 'playing';
    this.controlLock = false;
    this.compressing = false;

    this.jumpsUsed = 0;
    this.maxJumps = 2;

    // Estado visual del fluido.
    this.visualTime = 0;
    this.visualScaleX = 1;
    this.visualScaleY = 1;
    this.squashKickX = 0;
    this.squashKickY = 0;
    this.previousVelocityX = 0;
    this.previousVelocityY = 0;
    this.wasGrounded = false;
    this.lastTrailBurstAt = 0;

    this.mobileInput = {
      left: false,
      right: false,
      jump: false,
      jumpJustPressed: false,
      compress: false,
    };

    this.currentZones = [];
    this.densityZones = [];
    this.farParticles = [];
    this.fishSystem = null;
    this.jellyfishSystem = null;

    markPlaying('w1', 'l1');
  }

  create() {
    this.physics.world.setBounds(
      0,
      0,
      WORLD_W,
      WORLD_H
    );

    this.cameras.main.setBounds(
      0,
      0,
      WORLD_W,
      WORLD_H
    );

    this.cameras.main.setBackgroundColor('#020617');

    this.buildBackground();

    this.fishSystem = new FishSystem(this, {
      worldWidth: WORLD_W,
    });
    this.fishSystem.create(22);

    this.jellyfishSystem = new JellyfishSystem(this, {
      worldWidth: WORLD_W,
    });
    this.jellyfishSystem.create(6);

    this.buildLevelGeometry();
    this.buildCollectibles();
    this.buildObstacles();
    this.buildCurrents();
    this.buildCheckpoint();
    this.buildPortal();
    this.buildPlayer();
    this.setupInput();
    this.registerGameEvents();

    this.game.events.emit('level:start', {
      name: 'level.name',
    });

    this.game.events.emit(
      'volume:update',
      this.volume
    );

    this.game.events.emit(
      'drops:update',
      this.dropsCollected
    );

    this.game.events.emit(
      'ui:showTouchControls',
      shouldShowTouchControls()
    );

    this.events.once(
      Phaser.Scenes.Events.SHUTDOWN,
      () => this.cleanup()
    );
  }

  registerGameEvents() {
    this.onMobileInput = (payload) => {
      this.mobileInput = {
        ...this.mobileInput,
        ...payload,
      };
    };

    this.onPauseRequest = () => {
      this.togglePause();
    };

    this.onResume = () => {
      this.resumeGame();
    };

    this.onRestart = () => {
      this.restartLevel();
    };

    this.onRetryCheckpoint = () => {
      this.respawn();
    };

    this.onExit = () => {
      window.dispatchEvent(
        new CustomEvent('neon:exitToMenu')
      );
    };

    this.game.events.on(
      'ui:mobileInput',
      this.onMobileInput
    );

    this.game.events.on(
      'ui:pauseRequest',
      this.onPauseRequest
    );

    this.game.events.on(
      'ui:resume',
      this.onResume
    );

    this.game.events.on(
      'ui:restart',
      this.onRestart
    );

    this.game.events.on(
      'ui:retryCheckpoint',
      this.onRetryCheckpoint
    );

    this.game.events.on(
      'ui:exit',
      this.onExit
    );
  }

  buildBackground() {
    const quality = getSettings().graphicsQuality;

    const sky = this.add.graphics();

    sky.fillGradientStyle(
      0x020617,
      0x020617,
      0x0c4a6e,
      0x0e7490,
      1
    );

    sky.fillRect(
      0,
      0,
      WORLD_W,
      WORLD_H
    );

    sky.setScrollFactor(0.15);

    const rays = this.add.graphics();

    rays.setScrollFactor(0.25);
    rays.fillStyle(0x22d3ee, 0.06);

    for (let i = 0; i < 8; i += 1) {
      const x = 200 + i * 480;

      rays.fillTriangle(
        x,
        0,
        x - 40,
        WORLD_H,
        x + 60,
        WORLD_H
      );
    }

    this.tweens.add({
      targets: rays,
      alpha: {
        from: 0.7,
        to: 1,
      },
      duration: 3800,
      yoyo: true,
      repeat: -1,
    });

    const bubbleCount =
      quality === 'low'
        ? 20
        : quality === 'high'
          ? 60
          : 40;

    for (let i = 0; i < bubbleCount; i += 1) {
      const bubble = this.add.image(
        Math.random() * WORLD_W,
        Math.random() * WORLD_H,
        'bubble'
      );

      bubble.setScale(
        0.5 + Math.random() * 1.5
      );

      bubble.setAlpha(
        0.15 + Math.random() * 0.35
      );

      bubble.setScrollFactor(
        0.3 + Math.random() * 0.4
      );

      this.tweens.add({
        targets: bubble,
        y:
          bubble.y -
          100 -
          Math.random() * 150,
        duration:
          6000 +
          Math.random() * 6000,
        repeat: -1,
        yoyo: false,
        onRepeat: () => {
          bubble.y = WORLD_H + 20;
        },
      });
    }

    const decorationPositions = [
      180,
      460,
      720,
      1100,
      1500,
      1900,
      2350,
      2700,
      3100,
      3500,
      3720,
    ];

    decorationPositions.forEach(
      (x, index) => {
        const decoration = this.add.image(
          x,
          GROUND_Y + 6,
          'coralDeco'
        );

        decoration.setOrigin(0.5, 1);
        decoration.setScrollFactor(0.7);

        decoration.setScale(
          0.9 + Math.random() * 0.5
        );

        decoration.setAlpha(0.85);
        decoration.setDepth(-1);

        this.tweens.add({
          targets: decoration,
          angle: {
            from: -3,
            to: 3,
          },
          duration:
            3200 + index * 137,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    );

    const algaePositions = [
      260,
      540,
      900,
      1250,
      1700,
      2100,
      2500,
      2900,
      3300,
    ];

    algaePositions.forEach(
      (x, index) => {
        const algae = this.add.image(
          x,
          GROUND_Y,
          'algae'
        );

        algae.setOrigin(0.5, 1);

        algae.setScale(
          1 + Math.random() * 1.5
        );

        algae.setDepth(1);

        this.tweens.add({
          targets: algae,
          angle: {
            from: -8,
            to: 8,
          },
          duration:
            1800 + index * 90,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    );

    const particleCount =
      quality === 'low'
        ? 35
        : quality === 'high'
          ? 100
          : 70;

    for (let index = 0; index < particleCount; index += 1) {
      const particle = this.add.image(
        Phaser.Math.Between(0, WORLD_W),
        Phaser.Math.Between(20, GROUND_Y - 20),
        'particle'
      );

      particle.setDepth(0.2);
      particle.setScrollFactor(Phaser.Math.FloatBetween(0.35, 0.8));
      particle.setAlpha(Phaser.Math.FloatBetween(0.04, 0.16));
      particle.setScale(Phaser.Math.FloatBetween(0.15, 0.55));

      particle.waterData = {
        speed: Phaser.Math.FloatBetween(3, 10),
        phase: Math.random() * Math.PI * 2,
      };

      this.farParticles.push(particle);
    }
  }

  buildLevelGeometry() {
    this.solids =
      this.physics.add.staticGroup();

    for (
      let x = 0;
      x < WORLD_W;
      x += 64
    ) {
      const ground =
        this.solids.create(
          x + 32,
          GROUND_Y + 32,
          'ground'
        );

      ground.setDepth(0);
      ground.refreshBody();
    }

    const platforms = [
      {
        x: 640,
        y: 520,
        width: 2,
      },
      {
        x: 1080,
        y: 480,
        width: 3,
      },
      {
        x: 1520,
        y: 540,
        width: 2,
      },
      {
        x: 2200,
        y: 500,
        width: 3,
      },
      {
        x: 3200,
        y: 500,
        width: 2,
      },
      {
        x: 3480,
        y: 460,
        width: 2,
      },
    ];

    platforms.forEach((platform) => {
      for (
        let index = 0;
        index < platform.width;
        index += 1
      ) {
        const rock =
          this.solids.create(
            platform.x +
              index * 64,
            platform.y,
            'rock'
          );

        rock.refreshBody();
      }
    });

    const ceilingY =
      GROUND_Y - 40;

    for (
      let x = 2500;
      x < 2760;
      x += 64
    ) {
      const rock =
        this.solids.create(
          x + 32,
          ceilingY - 32,
          'rock'
        );

      rock.refreshBody();
    }

    for (
      let x = 2500;
      x < 2760;
      x += 64
    ) {
      const rock =
        this.solids.create(
          x + 32,
          ceilingY - 96,
          'rock'
        );

      rock.refreshBody();
    }

    this.crackGapY =
      ceilingY - 8;

    for (
      let y = GROUND_Y - 80;
      y > 320;
      y -= 64
    ) {
      const rock =
        this.solids.create(
          3800,
          y,
          'rock'
        );

      rock.refreshBody();
    }
  }

  isCollectiblePositionFree(
    x,
    y,
    margin = 22
  ) {
    if (!this.solids) {
      return true;
    }

    return !this.solids
      .getChildren()
      .some((solid) => {
        const bounds =
          solid.getBounds();

        return (
          x + margin >
            bounds.left &&
          x - margin <
            bounds.right &&
          y + margin >
            bounds.top &&
          y - margin <
            bounds.bottom
        );
      });
  }

  buildCollectibles() {
    this.drops =
      this.physics.add.group({
        allowGravity: false,
        immovable: true,
      });

    const positions = [
      { x: 280, y: 560 },
      { x: 380, y: 500 },
      { x: 500, y: 520 },
      { x: 660, y: 470 },
      { x: 1100, y: 430 },
      { x: 1650, y: 500 },
      { x: 1750, y: 460 },
      { x: 1850, y: 500 },
      { x: 2100, y: 450 },
      { x: 2280, y: 430 },
      { x: 2440, y: 580 },
      { x: 2820, y: 580 },
      { x: 3000, y: 470 },
      { x: 3140, y: 460 },
      { x: 3280, y: 420 },
      { x: 3520, y: 390 },
      { x: 3650, y: 520 },
    ];

    positions.forEach((position) => {
      let safeY = position.y;
      let attempts = 0;

      while (
        !this.isCollectiblePositionFree(
          position.x,
          safeY
        ) &&
        attempts < 8
      ) {
        safeY -= 32;
        attempts += 1;
      }

      if (
        !this.isCollectiblePositionFree(
          position.x,
          safeY
        )
      ) {
        console.warn(
          'Gota descartada por posición bloqueada:',
          position
        );

        return;
      }

      const drop =
        this.drops.create(
          position.x,
          safeY,
          'droplet'
        );

      drop.setDepth(3);

      const halo = this.add
        .image(
          position.x,
          safeY,
          'droplet'
        )
        .setScale(2.2)
        .setAlpha(0.25)
        .setTint(0x22d3ee)
        .setDepth(2);

      drop.halo = halo;

      this.tweens.add({
        targets: [
          drop,
          halo,
        ],
        y: safeY - 6,
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      this.tweens.add({
        targets: halo,
        scale: {
          from: 2,
          to: 2.6,
        },
        duration: 900,
        yoyo: true,
        repeat: -1,
      });
    });
  }

  buildObstacles() {
    this.urchins =
      this.physics.add.group({
        allowGravity: false,
        immovable: true,
      });

    const urchinPositions = [
      {
        x: 900,
        y: GROUND_Y - 16,
      },
      {
        x: 1300,
        y: GROUND_Y - 16,
      },
      {
        x: 1580,
        y: GROUND_Y - 16,
      },
      {
        x: 2350,
        y: GROUND_Y - 16,
      },
      {
        x: 3400,
        y: GROUND_Y - 16,
      },
      {
        x: 3600,
        y: GROUND_Y - 16,
      },
    ];

    urchinPositions.forEach(
      (position) => {
        const urchin =
          this.urchins.create(
            position.x,
            position.y,
            'urchin'
          );

        urchin.setOrigin(0.5, 1);
        urchin.setDepth(2);

        urchin.body
          .setSize(40, 40)
          .setOffset(12, 20);
      }
    );

    this.badCorals =
      this.physics.add.group({
        allowGravity: false,
        immovable: true,
      });

    const coralPositions = [
      {
        x: 1400,
        y: GROUND_Y - 16,
      },
      {
        x: 1460,
        y: GROUND_Y - 16,
      },
      {
        x: 2400,
        y: GROUND_Y - 16,
      },
    ];

    coralPositions.forEach(
      (position) => {
        const coral =
          this.badCorals.create(
            position.x,
            position.y,
            'coralBad'
          );

        coral.setOrigin(0.5, 1);
        coral.setDepth(2);

        coral.body
          .setSize(52, 40)
          .setOffset(6, 20);
      }
    );
  }

  buildCurrents() {
    this.currentZones = [
      {
        x1: 1580,
        x2: 1900,
        y1: 380,
        y2: GROUND_Y,
        force: -260,
        color: 0xef4444,
        direction: -1,
      },
      {
        x1: 2920,
        x2: 3320,
        y1: 380,
        y2: GROUND_Y,
        force: 240,
        color: 0x22d3ee,
        direction: 1,
      },
    ];

    this.currentZones.forEach(
      (zone) => {
        const width =
          zone.x2 - zone.x1;

        const height =
          zone.y2 - zone.y1;

        const overlay =
          this.add.rectangle(
            zone.x1 +
              width / 2,
            zone.y1 +
              height / 2,
            width,
            height,
            zone.color,
            0.08
          );

        overlay.setDepth(0.5);

        for (
          let i = 0;
          i < 12;
          i += 1
        ) {
          const streak =
            this.add.image(
              zone.x1 +
                Math.random() *
                  width,
              zone.y1 +
                Math.random() *
                  height,
              'currentStreak'
            );

          streak
            .setTint(zone.color)
            .setAlpha(0.6)
            .setDepth(1);

          if (
            zone.direction < 0
          ) {
            streak.setFlipX(true);
          }

          this.tweens.add({
            targets: streak,
            x:
              streak.x +
              zone.direction *
                120,
            duration:
              900 +
              Math.random() *
                400,
            repeat: -1,
            onRepeat: () => {
              streak.x =
                zone.x1 +
                Math.random() *
                  width;

              streak.y =
                zone.y1 +
                Math.random() *
                  height;
            },
          });
        }
      }
    );

    this.densityZones = [
      {
        x1: 2500,
        x2: 2760,
        y1: 400,
        y2: GROUND_Y,
        multiplier: 0.55,
      },
    ];
  }

  buildCheckpoint() {
    this.checkpointSprite =
      this.physics.add.staticImage(
        2000,
        GROUND_Y - 40,
        'checkpoint'
      );

    this.checkpointSprite.setDepth(2);
    this.checkpointActive = false;

    this.tweens.add({
      targets:
        this.checkpointSprite,
      y: GROUND_Y - 46,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  buildPortal() {
    this.portal =
      this.physics.add.staticImage(
        3720,
        GROUND_Y - 100,
        'portal'
      );

    this.portal.setDepth(2);

    this.portal.body.setCircle(
      50,
      14,
      14
    );

    this.tweens.add({
      targets: this.portal,
      scale: {
        from: 0.9,
        to: 1.1,
      },
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  buildPlayer() {
    this.playerGlow = this.add
      .image(
        this.checkpoint.x,
        this.checkpoint.y,
        'blobGlow'
      )
      .setDepth(4)
      .setScale(0.92)
      .setAlpha(0.85);

    this.tweens.add({
      targets:
        this.playerGlow,
      alpha: {
        from: 0.65,
        to: 0.9,
      },
      duration: 900,
      yoyo: true,
      repeat: -1,
    });

    this.player =
      this.physics.add.sprite(
        this.checkpoint.x,
        this.checkpoint.y,
        'blob'
      );

    this.player.setDepth(5);
    this.player.setBounce(0.15);

    this.player.setCollideWorldBounds(
      true
    );

    // Hitbox estable.
    this.player.body
      .setSize(40, 40)
      .setOffset(28, 28);

    this.player.setDragX(600);

    this.player.setMaxVelocity(
      320,
      700
    );

    this.player.setDisplaySize(
      58,
      58
    );

    this.player.setRotation(0);

    this.eyeL = this.add
      .circle(
        0,
        0,
        4,
        0x02121b
      )
      .setDepth(6);

    this.eyeR = this.add
      .circle(
        0,
        0,
        4,
        0x02121b
      )
      .setDepth(6);

    this.pupilL = this.add
      .circle(
        0,
        0,
        1.7,
        0xe0f7ff
      )
      .setDepth(7);

    this.pupilR = this.add
      .circle(
        0,
        0,
        1.7,
        0xe0f7ff
      )
      .setDepth(7);

    const quality =
      getSettings().graphicsQuality;

    const baseFrequency =
      quality === 'low'
        ? 180
        : quality === 'high'
          ? 80
          : 120;

    this.particles =
      this.add.particles(
        0,
        0,
        'particle',
        {
          lifespan: {
            min: 420,
            max: 760,
          },
          speed: {
            min: 12,
            max: 48,
          },
          angle: {
            min: 150,
            max: 210,
          },
          scale: {
            start: 0.85,
            end: 0,
          },
          alpha: {
            start: 0.55,
            end: 0,
          },
          tint: [
            0x22d3ee,
            0xa5f3fc,
            0xecfeff,
          ],
          frequency:
            baseFrequency,
          follow:
            this.player,
          followOffset: {
            x: 0,
            y: 5,
          },
          quantity:
            quality === 'high'
              ? 2
              : 1,
        }
      );

    this.particles.setDepth(4);

    this.physics.add.collider(
      this.player,
      this.solids,
      this.onLand,
      null,
      this
    );

    this.physics.add.overlap(
      this.player,
      this.drops,
      this.collectDrop,
      null,
      this
    );

    this.physics.add.overlap(
      this.player,
      this.urchins,
      this.hitUrchin,
      null,
      this
    );

    this.physics.add.overlap(
      this.player,
      this.badCorals,
      this.touchBadCoral,
      null,
      this
    );

    this.physics.add.overlap(
      this.player,
      this.checkpointSprite,
      this.hitCheckpoint,
      null,
      this
    );

    this.physics.add.overlap(
      this.player,
      this.portal,
      this.enterPortal,
      null,
      this
    );

    this.cameras.main.startFollow(
      this.player,
      true,
      0.08,
      0.08
    );

    this.cameras.main.setDeadzone(
      120,
      80
    );
  }

  setupInput() {
    this.cursors =
      this.input.keyboard.createCursorKeys();

    this.keys =
      this.input.keyboard.addKeys({
        a:
          Phaser.Input.Keyboard
            .KeyCodes.A,
        d:
          Phaser.Input.Keyboard
            .KeyCodes.D,
        w:
          Phaser.Input.Keyboard
            .KeyCodes.W,
        s:
          Phaser.Input.Keyboard
            .KeyCodes.S,
        space:
          Phaser.Input.Keyboard
            .KeyCodes.SPACE,
        r:
          Phaser.Input.Keyboard
            .KeyCodes.R,
        esc:
          Phaser.Input.Keyboard
            .KeyCodes.ESC,
      });

    this.keys.esc.on(
      'down',
      () => this.togglePause()
    );

    this.keys.r.on(
      'down',
      () => this.respawn()
    );
  }

  update(time, delta) {
    const particleDelta = Math.min(delta, 40) / 1000;

    this.farParticles.forEach((particle) => {
      if (!particle.active || !particle.waterData) {
        return;
      }

      particle.y -= particle.waterData.speed * particleDelta;
      particle.x += Math.sin(time * 0.0006 + particle.waterData.phase) * 0.12;

      if (particle.y < -10) {
        particle.y = GROUND_Y;
        particle.x = Phaser.Math.Between(0, WORLD_W);
      }
    });

    if (this.fishSystem) {
      this.fishSystem.update(time, delta);
    }

    if (this.jellyfishSystem) {
      this.jellyfishSystem.update(time, delta);
    }

    if (
      this.levelState !==
        'playing' ||
      this.controlLock
    ) {
      this.updatePlayerVisuals(
        delta,
        0
      );

      return;
    }

    const leftDown =
      this.cursors.left.isDown ||
      this.keys.a.isDown ||
      this.mobileInput.left;

    const rightDown =
      this.cursors.right.isDown ||
      this.keys.d.isDown ||
      this.mobileInput.right;

    const jumpDown =
      Phaser.Input.Keyboard.JustDown(
        this.cursors.up
      ) ||
      Phaser.Input.Keyboard.JustDown(
        this.keys.w
      ) ||
      Phaser.Input.Keyboard.JustDown(
        this.keys.space
      ) ||
      this.mobileInput
        .jumpJustPressed;

    const compressDown =
      this.cursors.down.isDown ||
      this.keys.s.isDown ||
      this.mobileInput.compress;

    if (
      this.mobileInput
        .jumpJustPressed
    ) {
      this.mobileInput.jumpJustPressed =
        false;
    }

    if (
      compressDown !==
      this.compressing
    ) {
      this.compressing =
        compressDown;

      if (this.compressing) {
        this.player.body
          .setSize(50, 20)
          .setOffset(23, 38);
      } else {
        this.player.body
          .setSize(40, 40)
          .setOffset(28, 28);
      }
    }

    const onGround =
      this.player.body
        .blocked.down ||
      this.player.body
        .touching.down;

    if (onGround) {
      this.jumpsUsed = 0;
    }

    let speedMultiplier = 1;

    for (
      const zone of
      this.densityZones
    ) {
      if (
        this.player.x >=
          zone.x1 &&
        this.player.x <=
          zone.x2 &&
        this.player.y >=
          zone.y1 &&
        this.player.y <=
          zone.y2
      ) {
        speedMultiplier =
          Math.min(
            speedMultiplier,
            zone.multiplier
          );
      }
    }

    if (this.compressing) {
      speedMultiplier *= 0.7;
    }

    const acceleration =
      (onGround ? 900 : 700) *
      speedMultiplier;

    let inputDirection = 0;

    if (
      leftDown &&
      !rightDown
    ) {
      inputDirection = -1;
    } else if (
      rightDown &&
      !leftDown
    ) {
      inputDirection = 1;
    }

    if (
      inputDirection !== 0
    ) {
      this.player.setAccelerationX(
        inputDirection *
          acceleration
      );

      this.player.setDragX(200);
    } else {
      this.player.setAccelerationX(
        0
      );

      this.player.setDragX(1000);
    }

    const baseMaxVelocity = 320;

    this.player.setMaxVelocity(
      baseMaxVelocity *
        speedMultiplier,
      800
    );

    if (
      jumpDown &&
      !this.compressing &&
      this.jumpsUsed <
        this.maxJumps
    ) {
      const isFirstJump =
        this.jumpsUsed === 0;

      this.player.setVelocityY(
        isFirstJump
          ? -500
          : -430
      );

      this.jumpsUsed += 1;

      this.squash(
        isFirstJump
          ? 1.2
          : 1.12,
        isFirstJump
          ? 0.75
          : 0.84
      );

      this.emitJumpSplash(
        isFirstJump
      );

      AudioSystem.playEffect(
        'jump'
      );
    } else if (
      jumpDown &&
      onGround &&
      this.compressing
    ) {
      this.player.setVelocityY(
        -260
      );

      this.jumpsUsed = 1;

      this.emitJumpSplash(
        false,
        5
      );
    }

    for (
      const zone of
      this.currentZones
    ) {
      if (
        this.player.x >=
          zone.x1 &&
        this.player.x <=
          zone.x2 &&
        this.player.y >=
          zone.y1 &&
        this.player.y <=
          zone.y2
      ) {
        this.player.body
          .velocity.x +=
          zone.force *
          (delta / 1000);

        const cap =
          Math.abs(
            zone.force
          ) +
          baseMaxVelocity;

        this.player.body
          .velocity.x =
          Phaser.Math.Clamp(
            this.player.body
              .velocity.x,
            -cap,
            cap
          );
      }
    }

    this.updatePlayerVisuals(
      delta,
      inputDirection
    );

    this.updateLiquidTrail(
      delta,
      inputDirection
    );

    if (
      this.player.y >
      WORLD_H + 100
    ) {
      this.applyDamage(
        MAX_VOLUME
      );
    }
  }

  updatePlayerVisuals(
    delta,
    inputDirection
  ) {
    const body =
      this.player.body;

    const velocityX =
      body.velocity.x;

    const velocityY =
      body.velocity.y;

    const speedX =
      Math.abs(velocityX);

    const onGround =
      body.blocked.down ||
      body.touching.down;

    const frameFactor =
      Math.min(
        delta / 16.6667,
        2
      );

    const smoothFactor =
      1 -
      Math.pow(
        0.78,
        frameFactor
      );

    this.visualTime +=
      delta * 0.006;

    let targetScaleX = 1;
    let targetScaleY = 1;

    const idleWave =
      Math.sin(
        this.visualTime
      ) * 0.035;

    const secondaryWave =
      Math.sin(
        this.visualTime *
          1.7 +
          1.2
      ) * 0.018;

    targetScaleX += idleWave;

    targetScaleY -=
      idleWave * 0.7;

    targetScaleY +=
      secondaryWave;

    const horizontalStretch =
      Phaser.Math.Clamp(
        speedX / 900,
        0,
        0.24
      );

    targetScaleX +=
      horizontalStretch;

    targetScaleY -=
      horizontalStretch *
      0.48;

    if (
      !onGround &&
      velocityY < -50
    ) {
      const jumpStretch =
        Phaser.Math.Clamp(
          Math.abs(
            velocityY
          ) / 1500,
          0,
          0.24
        );

      targetScaleY +=
        jumpStretch;

      targetScaleX -=
        jumpStretch * 0.42;
    }

    if (
      !onGround &&
      velocityY > 80
    ) {
      const fallStretch =
        Phaser.Math.Clamp(
          velocityY / 1600,
          0,
          0.28
        );

      targetScaleY +=
        fallStretch;

      targetScaleX -=
        fallStretch * 0.36;
    }

    if (this.compressing) {
      targetScaleX =
        1.48 +
        Math.sin(
          this.visualTime *
            1.5
        ) *
          0.025;

      targetScaleY =
        0.54 +
        Math.cos(
          this.visualTime *
            1.8
        ) *
          0.015;
    }

    if (
      !this.wasGrounded &&
      onGround &&
      this.previousVelocityY >
        180
    ) {
      const impact =
        Phaser.Math.Clamp(
          this.previousVelocityY /
            700,
          0.3,
          1
        );

      this.squashKickX =
        0.22 * impact;

      this.squashKickY =
        -0.28 * impact;

      this.emitLandingSplash(
        impact
      );

      AudioSystem.playEffect(
        'land'
      );
    }

    targetScaleX +=
      this.squashKickX;

    targetScaleY +=
      this.squashKickY;

    this.squashKickX *=
      Math.pow(
        0.76,
        frameFactor
      );

    this.squashKickY *=
      Math.pow(
        0.76,
        frameFactor
      );

    this.visualScaleX =
      Phaser.Math.Linear(
        this.visualScaleX,
        targetScaleX,
        smoothFactor
      );

    this.visualScaleY =
      Phaser.Math.Linear(
        this.visualScaleY,
        targetScaleY,
        smoothFactor
      );

    const baseVisualScale =
      58 / 96;

    const volumeScale =
      0.82 +
      (this.volume /
        MAX_VOLUME) *
        0.18;

    this.player.setScale(
      baseVisualScale *
        volumeScale *
        this.visualScaleX,
      baseVisualScale *
        volumeScale *
        this.visualScaleY
    );

    const horizontalAcceleration =
      velocityX -
      this.previousVelocityX;

    let targetRotation =
      Phaser.Math.Clamp(
        horizontalAcceleration *
          0.0025,
        -0.16,
        0.16
      );

    if (!onGround) {
      targetRotation +=
        Phaser.Math.Clamp(
          velocityX / 1800,
          -0.08,
          0.08
        );
    }

    this.player.rotation =
      Phaser.Math.Linear(
        this.player.rotation,
        targetRotation,
        0.1 * frameFactor
      );

    const glowTargetX =
      this.player.x -
      Phaser.Math.Clamp(
        velocityX * 0.025,
        -10,
        10
      );

    const glowTargetY =
      this.player.y -
      Phaser.Math.Clamp(
        velocityY * 0.01,
        -5,
        7
      );

    this.playerGlow.x =
      Phaser.Math.Linear(
        this.playerGlow.x,
        glowTargetX,
        0.18 * frameFactor
      );

    this.playerGlow.y =
      Phaser.Math.Linear(
        this.playerGlow.y,
        glowTargetY,
        0.18 * frameFactor
      );

    this.playerGlow.setScale(
      0.82 *
        volumeScale *
        (1 +
          Math.sin(
            this.visualTime *
              1.25
          ) *
            0.04)
    );

    const eyeWave =
      Math.sin(
        this.visualTime *
          1.4
      ) * 1;

    const eyeOffsetY =
      this.compressing
        ? -1
        : -4.5 *
          this.visualScaleY;

    const eyeSpread =
      this.compressing
        ? 10
        : 7.5 *
          this.visualScaleX;

    this.eyeL.x =
      this.player.x -
      eyeSpread;

    this.eyeR.x =
      this.player.x +
      eyeSpread;

    this.eyeL.y =
      this.player.y +
      eyeOffsetY +
      eyeWave;

    this.eyeR.y =
      this.player.y +
      eyeOffsetY +
      eyeWave;

    const lookX =
      Phaser.Math.Clamp(
        inputDirection *
          1.7 +
          velocityX / 210,
        -2.5,
        2.5
      );

    const lookY =
      Phaser.Math.Clamp(
        velocityY / 300,
        -1.7,
        1.7
      );

    this.pupilL.x =
      this.eyeL.x +
      lookX;

    this.pupilR.x =
      this.eyeR.x +
      lookX;

    this.pupilL.y =
      this.eyeL.y +
      lookY;

    this.pupilR.y =
      this.eyeR.y +
      lookY;

    if (
      this.time.now <
      this.invulnerableUntil
    ) {
      const flash =
        Math.floor(
          this.time.now / 80
        ) %
          2 ===
        0;

      this.player.setAlpha(
        flash ? 1 : 0.4
      );

      this.playerGlow.setAlpha(
        flash ? 0.85 : 0.25
      );
    } else {
      this.player.setAlpha(1);

      this.playerGlow.setAlpha(
        0.78
      );
    }

    this.previousVelocityX =
      velocityX;

    this.previousVelocityY =
      velocityY;

    this.wasGrounded =
      onGround;
  }

  updateLiquidTrail(
    _delta,
    inputDirection
  ) {
    if (!this.particles) {
      return;
    }

    const speed =
      Math.abs(
        this.player.body
          .velocity.x
      );

    const verticalSpeed =
      Math.abs(
        this.player.body
          .velocity.y
      );

    const quality =
      getSettings()
        .graphicsQuality;

    let frequency =
      quality === 'low'
        ? 220
        : quality === 'high'
          ? 95
          : 140;

    if (
      speed > 250 ||
      verticalSpeed > 320
    ) {
      frequency =
        quality === 'low'
          ? 100
          : quality ===
                'high'
            ? 32
            : 55;
    } else if (
      speed > 100
    ) {
      frequency =
        quality === 'low'
          ? 150
          : quality ===
                'high'
            ? 58
            : 85;
    }

    this.particles.setFrequency(
      frequency
    );

    const trailDirection =
      inputDirection !== 0
        ? -inputDirection
        : -Math.sign(
            this.player.body
              .velocity.x
          );

    const offsetX =
      trailDirection === 0
        ? 0
        : trailDirection * 14;

    if (this.particles.followOffset) {
  this.particles.followOffset.x = offsetX;
  this.particles.followOffset.y = 5;
}

    if (
      speed > 270 &&
      this.time.now -
        this.lastTrailBurstAt >
        180
    ) {
      this.lastTrailBurstAt =
        this.time.now;

      this.emitMiniDroplet(
        this.player.x +
          offsetX,
        this.player.y + 5,
        trailDirection
      );
    }
  }

  emitMiniDroplet(
    x,
    y,
    direction
  ) {
    const quality =
      getSettings()
        .graphicsQuality;

    if (quality === 'low') {
      return;
    }

    const droplet = this.add
      .image(
        x,
        y +
          Phaser.Math.Between(
            -4,
            5
          ),
        'particle'
      )
      .setDepth(4)
      .setScale(
        Phaser.Math.FloatBetween(
          0.45,
          0.8
        )
      )
      .setAlpha(0.8)
      .setTint(0xa5f3fc);

    this.tweens.add({
      targets: droplet,
      x:
        droplet.x +
        direction *
          Phaser.Math.Between(
            10,
            22
          ),
      y:
        droplet.y +
        Phaser.Math.Between(
          5,
          14
        ),
      alpha: 0,
      scale: 0.1,
      duration:
        Phaser.Math.Between(
          260,
          420
        ),
      ease: 'Quad.easeOut',
      onComplete: () => {
        droplet.destroy();
      },
    });
  }

  emitJumpSplash(
    isFirstJump,
    quantityOverride = null
  ) {
    const quality =
      getSettings()
        .graphicsQuality;

    const quantity =
      quantityOverride ??
      (quality === 'low'
        ? 4
        : isFirstJump
          ? 10
          : 7);

    const burst =
      this.add.particles(
        this.player.x,
        this.player.y + 14,
        'particle',
        {
          lifespan: {
            min: 280,
            max: 520,
          },
          speed: {
            min: 35,
            max:
              isFirstJump
                ? 110
                : 80,
          },
          angle: {
            min: 205,
            max: 335,
          },
          gravityY: 180,
          scale: {
            start: 0.8,
            end: 0,
          },
          alpha: {
            start: 0.8,
            end: 0,
          },
          tint: [
            0x22d3ee,
            0xa5f3fc,
            0xecfeff,
          ],
          quantity,
          emitting: false,
        }
      );

    burst.setDepth(4);
    burst.explode(quantity);

    this.time.delayedCall(
      650,
      () => burst.destroy()
    );
  }

  emitLandingSplash(
    impact
  ) {
    const quality =
      getSettings()
        .graphicsQuality;

    const quantity =
      quality === 'low'
        ? 5
        : quality === 'high'
          ? 18
          : 11;

    const burst =
      this.add.particles(
        this.player.x,
        this.player.y + 15,
        'particle',
        {
          lifespan: {
            min: 320,
            max: 700,
          },
          speed: {
            min: 45,
            max:
              110 +
              impact * 100,
          },
          angle: {
            min: 195,
            max: 345,
          },
          gravityY: 220,
          scale: {
            start:
              0.75 +
              impact * 0.35,
            end: 0,
          },
          alpha: {
            start: 0.9,
            end: 0,
          },
          tint: [
            0x22d3ee,
            0xa5f3fc,
            0xecfeff,
          ],
          quantity,
          emitting: false,
        }
      );

    burst.setDepth(4);
    burst.explode(quantity);

    this.time.delayedCall(
      800,
      () => burst.destroy()
    );
  }

  squash(
    scaleX,
    scaleY
  ) {
    this.squashKickX =
      scaleX - 1;

    this.squashKickY =
      scaleY - 1;
  }

  onLand() {
    // El aterrizaje se detecta en
    // updatePlayerVisuals().
  }

  collectDrop(
    _player,
    drop
  ) {
    if (!drop.active) {
      return;
    }

    this.volume = Math.min(
      MAX_VOLUME,
      this.volume + 10
    );

    this.dropsCollected += 1;

    this.game.events.emit(
      'volume:update',
      this.volume
    );

    this.game.events.emit(
      'drops:update',
      this.dropsCollected
    );

    AudioSystem.playEffect(
      'collect'
    );

    AudioSystem.vibrate(15);

    const burst =
      this.add.particles(
        drop.x,
        drop.y,
        'particle',
        {
          lifespan: 500,
          speed: {
            min: 80,
            max: 160,
          },
          scale: {
            start: 1.25,
            end: 0,
          },
          alpha: {
            start: 1,
            end: 0,
          },
          tint: [
            0x22d3ee,
            0xa5f3fc,
            0xecfeff,
          ],
          quantity: 12,
          emitting: false,
        }
      );

    burst.setDepth(4);
    burst.explode(12);

    this.time.delayedCall(
      600,
      () => burst.destroy()
    );

    if (drop.halo) {
      drop.halo.destroy();
    }

    drop.disableBody(
      true,
      true
    );
  }

  hitUrchin() {
    this.applyDamage(15);

    const direction =
      this.player.body
        .velocity.x >= 0
        ? -1
        : 1;

    this.player.setVelocityX(
      direction * 260
    );

    this.player.setVelocityY(
      -260
    );
  }

  touchBadCoral() {
    if (
      this.time.now <
      this.invulnerableUntil
    ) {
      return;
    }

    this.volume = Math.max(
      0,
      this.volume - 3
    );

    this.game.events.emit(
      'volume:update',
      this.volume
    );

    this.invulnerableUntil =
      this.time.now + 250;

    if (this.volume <= 0) {
      this.defeat();
    }
  }

  applyDamage(amount) {
    if (
      this.time.now <
      this.invulnerableUntil
    ) {
      return;
    }

    this.volume = Math.max(
      0,
      this.volume - amount
    );

    this.game.events.emit(
      'volume:update',
      this.volume
    );

    this.invulnerableUntil =
      this.time.now + 1000;

    this.cameras.main.shake(
      180,
      0.008
    );

    AudioSystem.vibrate([
      30,
      40,
      30,
    ]);

    if (this.volume <= 0) {
      this.defeat();
    }
  }

  hitCheckpoint() {
    if (
      this.checkpointActive
    ) {
      return;
    }

    this.checkpointActive = true;

    this.checkpoint = {
      x:
        this.checkpointSprite.x,
      y:
        this.checkpointSprite.y -
        40,
      volume: this.volume,
    };

    this.checkpointSprite.setTexture(
      'checkpointActive'
    );

    this.game.events.emit(
      'ui:toast',
      'level.checkpoint'
    );

    const burst =
      this.add.particles(
        this.checkpointSprite.x,
        this.checkpointSprite.y,
        'particle',
        {
          lifespan: 800,
          speed: {
            min: 100,
            max: 200,
          },
          scale: {
            start: 1.5,
            end: 0,
          },
          alpha: {
            start: 1,
            end: 0,
          },
          tint: [
            0x22d3ee,
            0xecfeff,
          ],
          quantity: 30,
          emitting: false,
        }
      );

    burst.explode(30);

    this.time.delayedCall(
      900,
      () => burst.destroy()
    );

    AudioSystem.vibrate(20);
  }

  enterPortal() {
    if (
      this.levelState !==
      'playing'
    ) {
      return;
    }

    this.levelState =
      'victory';

    this.controlLock = true;

    this.player.setVelocity(
      0,
      0
    );

    this.player.setAcceleration(
      0,
      0
    );

    this.tweens.add({
      targets: [
        this.player,
        this.playerGlow,
        this.eyeL,
        this.eyeR,
        this.pupilL,
        this.pupilR,
      ],
      x: this.portal.x,
      y: this.portal.y,
      duration: 800,
      ease: 'Cubic.easeIn',
    });

    this.tweens.add({
      targets: [
        this.player,
        this.playerGlow,
      ],
      scale: 0,
      alpha: 0.2,
      duration: 800,
      onComplete: () => {
        completeLevel(
          'w1',
          'l1',
          {
            drops:
              this.dropsCollected,
            volume:
              this.volume,
          }
        );

        this.game.events.emit(
          'level:victory',
          {
            drops:
              this.dropsCollected,
            volume:
              this.volume,
          }
        );
      },
    });
  }

  defeat() {
    if (
      this.levelState !==
      'playing'
    ) {
      return;
    }

    this.levelState =
      'defeat';

    this.controlLock = true;

    this.player.setVelocity(
      0,
      0
    );

    this.player.setAcceleration(
      0,
      0
    );

    const burst =
      this.add.particles(
        this.player.x,
        this.player.y,
        'particle',
        {
          lifespan: 1500,
          speed: {
            min: 100,
            max: 260,
          },
          scale: {
            start: 2,
            end: 0,
          },
          alpha: {
            start: 1,
            end: 0,
          },
          tint: [
            0x22d3ee,
            0xa5f3fc,
            0xecfeff,
            0x60a5fa,
          ],
          quantity: 60,
          emitting: false,
        }
      );

    burst.explode(60);

    this.player.setVisible(
      false
    );

    this.playerGlow.setVisible(
      false
    );

    this.eyeL.setVisible(false);
    this.eyeR.setVisible(false);
    this.pupilL.setVisible(false);
    this.pupilR.setVisible(false);

    AudioSystem.vibrate([
      80,
      60,
      80,
    ]);

    this.time.delayedCall(
      500,
      () => {
        this.game.events.emit(
          'level:defeat'
        );
      }
    );
  }

  respawn() {
    if (
      this.levelState ===
      'victory'
    ) {
      return;
    }

    this.levelState =
      'playing';

    this.controlLock = false;

    this.volume =
      this.checkpoint.volume ||
      MAX_VOLUME;

    this.game.events.emit(
      'volume:update',
      this.volume
    );

    this.player.setPosition(
      this.checkpoint.x,
      this.checkpoint.y
    );

    this.player.setVelocity(
      0,
      0
    );

    this.player.setAcceleration(
      0,
      0
    );

    this.player.setVisible(true);

    this.playerGlow.setVisible(
      true
    );

    this.eyeL.setVisible(true);
    this.eyeR.setVisible(true);
    this.pupilL.setVisible(true);
    this.pupilR.setVisible(true);

    this.jumpsUsed = 0;
    this.compressing = false;

    this.visualScaleX = 1;
    this.visualScaleY = 1;

    this.squashKickX = 0;
    this.squashKickY = 0;

    this.player.body
      .setSize(40, 40)
      .setOffset(28, 28);

    this.invulnerableUntil =
      this.time.now + 800;

    this.game.events.emit(
      'ui:hideDefeat'
    );
  }

  restartLevel() {
    this.scene.restart();
  }

  togglePause() {
    if (
      this.levelState ===
        'victory' ||
      this.levelState ===
        'defeat'
    ) {
      return;
    }

    if (
      this.scene.isPaused()
    ) {
      this.scene.resume();

      this.game.events.emit(
        'ui:hidePause'
      );
    } else {
      this.scene.pause();

      this.game.events.emit(
        'ui:showPause'
      );
    }
  }

  resumeGame() {
    if (
      this.scene.isPaused()
    ) {
      this.scene.resume();
    }
  }

  cleanup() {
    if (this.fishSystem) {
      this.fishSystem.destroy();
      this.fishSystem = null;
    }

    if (this.jellyfishSystem) {
      this.jellyfishSystem.destroy();
      this.jellyfishSystem = null;
    }

    this.farParticles.forEach((particle) => {
      if (particle?.active) {
        particle.destroy();
      }
    });
    this.farParticles = [];

    this.game.events.off(
      'ui:mobileInput',
      this.onMobileInput
    );

    this.game.events.off(
      'ui:pauseRequest',
      this.onPauseRequest
    );

    this.game.events.off(
      'ui:resume',
      this.onResume
    );

    this.game.events.off(
      'ui:restart',
      this.onRestart
    );

    this.game.events.off(
      'ui:retryCheckpoint',
      this.onRetryCheckpoint
    );

    this.game.events.off(
      'ui:exit',
      this.onExit
    );
  }
}